import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "selectedSlots",
    "modeInput",
    "interactionModeInput",
    "sessionLengthInput",
    "slotButton",
    "sessionModeButton",
    "quarterHourModeButton",
    "weekSelect",
    "availabilityInteractionButton",
    "unavailabilityInteractionButton",
    "unavailabilityForm",
    "unavailabilityWeekStart",
    "unavailabilityDayInput",
    "unavailabilitySlotInput",
    "unavailabilityReasonInput"
  ]

  static values = {
    initialSlots: String,
    slotsPerDay: Number
  }

  connect() {
    this.selected = new Set(
      (this.initialSlotsValue || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
    )

    this.render()
  }

  setSessionMode() {
    this.modeInputTarget.value = "session"
    this.renderModeButtons()
  }

  setQuarterHourMode() {
    this.modeInputTarget.value = "fifteen"
    this.renderModeButtons()
  }

  setAvailabilityInteractionMode() {
    this.interactionModeInputTarget.value = "availability"
    this.renderInteractionButtons()
  }

  setUnavailabilityInteractionMode() {
    this.interactionModeInputTarget.value = "unavailability"
    this.renderInteractionButtons()
  }

  syncWeekStart() {
    const hiddenWeekStart = this.element.querySelector("input[name='week_start']")
    if (hiddenWeekStart) hiddenWeekStart.value = this.weekSelectTarget.value
  }

  applySelectedWeek() {
    this.syncWeekStart()
    this.element.requestSubmit()
  }

  toggleSlot(event) {
    const button = event.currentTarget
    const day = Number(button.dataset.day)
    const slot = Number(button.dataset.slot)

    if (this.interactionModeInputTarget.value === "unavailability") {
      this.createUnavailability({ day, slot })
      return
    }

    if (this.modeInputTarget.value === "fifteen") {
      this.toggleKeys([this.slotKey(day, slot)])
    } else {
      this.toggleKeys(this.sessionKeys(day, slot))
    }

    this.render()
  }

  toggleKeys(keys) {
    if (keys.length === 0) return

    const everySelected = keys.every((key) => this.selected.has(key))

    keys.forEach((key) => {
      if (everySelected) {
        this.selected.delete(key)
      } else {
        this.selected.add(key)
      }
    })
  }

  sessionKeys(day, slot) {
    const sessionLength = Math.max(Number(this.sessionLengthInputTarget.value) || 1, 1)
    const slots = []

    for (let offset = 0; offset < sessionLength; offset += 1) {
      const currentSlot = slot + offset
      if (currentSlot >= this.slotsPerDayValue) break
      slots.push(this.slotKey(day, currentSlot))
    }

    return slots
  }

  slotKey(day, slot) {
    return `${day}-${slot}`
  }

  dayHeadingClicked(event) {
    if (this.interactionModeInputTarget.value !== "unavailability") return

    const day = Number(event.currentTarget.dataset.day)
    this.createUnavailability({ day })
  }

  createUnavailability({ day, slot }) {
    const reason = window.prompt("Why is this venue unavailable?")
    if (!reason || reason.trim() === "") return

    this.unavailabilityDayInputTarget.value = String(day)
    this.unavailabilitySlotInputTarget.value = Number.isInteger(slot) ? String(slot) : ""
    this.unavailabilityReasonInputTarget.value = reason.trim()
    this.unavailabilityWeekStartTarget.value = this.weekSelectTarget.value
    this.unavailabilityFormTarget.requestSubmit()
  }

  render() {
    this.selectedSlotsTarget.value = Array.from(this.selected).sort().join(",")
    this.renderModeButtons()
    this.renderInteractionButtons()

    this.slotButtonTargets.forEach((button) => {
      const key = this.slotKey(button.dataset.day, button.dataset.slot)
      const selected = this.selected.has(key)

      button.setAttribute("aria-pressed", selected ? "true" : "false")
      button.classList.toggle("bg-[var(--theme-brand-primary)]", selected)
      button.classList.toggle("border-[var(--theme-brand-primary)]", selected)
      button.classList.toggle("bg-stone-100", !selected)
      button.classList.toggle("border-stone-200", !selected)
      button.classList.toggle("shadow-sm", selected)
    })
  }

  renderModeButtons() {
    const sessionSelected = this.modeInputTarget.value !== "fifteen"

    this.sessionModeButtonTarget.classList.toggle("bg-stone-900", sessionSelected)
    this.sessionModeButtonTarget.classList.toggle("text-white", sessionSelected)
    this.sessionModeButtonTarget.classList.toggle("border-stone-900", sessionSelected)
    this.quarterHourModeButtonTarget.classList.toggle("bg-stone-900", !sessionSelected)
    this.quarterHourModeButtonTarget.classList.toggle("text-white", !sessionSelected)
    this.quarterHourModeButtonTarget.classList.toggle("border-stone-900", !sessionSelected)
  }

  renderInteractionButtons() {
    const availabilitySelected = this.interactionModeInputTarget.value !== "unavailability"

    this.availabilityInteractionButtonTarget.classList.toggle("bg-stone-900", availabilitySelected)
    this.availabilityInteractionButtonTarget.classList.toggle("text-white", availabilitySelected)
    this.availabilityInteractionButtonTarget.classList.toggle("border-stone-900", availabilitySelected)
    this.unavailabilityInteractionButtonTarget.classList.toggle("bg-rose-700", !availabilitySelected)
    this.unavailabilityInteractionButtonTarget.classList.toggle("text-white", !availabilitySelected)
    this.unavailabilityInteractionButtonTarget.classList.toggle("border-rose-700", !availabilitySelected)
  }
}
